import {
  EyeIcon,
  EyeOffIcon,
  Settings01Icon,
} from '@langchain/untitled-ui-icons';

import { IconButton } from '@/design-system';
import { FilterViewSchema } from '@/types/schema';

import { Popover, PopoverContent, PopoverTrigger } from '../../Popover';
import { DEFAULT_FILTERS, VIEW_TO_LABEL } from '../constants';
import { DeleteSavedFilterButton } from './DeleteFilterButton';
import { EditFilterPopover } from './EditFilterButton';

const ToggleButton = (props: { onToggle: () => void; active: boolean }) => {
  return (
    <IconButton
      icon={props.active ? EyeIcon : EyeOffIcon}
      label={props.active ? 'Hide filter' : 'Show filter'}
      onClick={props.onToggle}
      color="secondary"
      variant="plain"
    />
  );
};

type FilterSettingProps =
  | {
      defaultFilter: string;
      onToggleDefaultFilter: (defaultFilter: string) => void;
      active: boolean;
    }
  | {
      filterView: FilterViewSchema;
      onToggleSwitch: () => void;
    };

const FilterSetting = (props: FilterSettingProps) => {
  const fv = 'filterView' in props ? props.filterView : undefined;
  const onToggleSwitch =
    'onToggleSwitch' in props ? props.onToggleSwitch : undefined;

  const defaultFilter =
    'defaultFilter' in props ? props.defaultFilter : undefined;
  const onToggleDefaultFilter =
    'onToggleDefaultFilter' in props ? props.onToggleDefaultFilter : undefined;
  const active = 'active' in props ? props.active : false;

  return (
    <div className="flex items-start justify-between gap-1.5 text-sm font-medium text-tertiary">
      <div>
        <div>
          {fv?.display_name ??
            (defaultFilter ? VIEW_TO_LABEL[defaultFilter] : '')}
        </div>
        {fv?.description && (
          <div className="text-xs font-normal text-tertiary">
            {fv.description}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        {fv && (
          <EditFilterPopover
            filterView={fv}
            popoverPlacement={{
              side: 'bottom',
              align: 'start',
              sideOffset: 8,
            }}
          />
        )}
        {fv ? (
          <DeleteSavedFilterButton
            filterView={fv}
            setFilterBarOpen={() => {}}
            onToggleSwitch={onToggleSwitch}
          />
        ) : defaultFilter && onToggleDefaultFilter ? (
          <ToggleButton
            onToggle={() => onToggleDefaultFilter(defaultFilter)}
            active={active}
          />
        ) : null}
      </div>
    </div>
  );
};

export const FilterSettings = (props: {
  savedFilters: FilterViewSchema[];
  onToggleSwitch: () => void;
  defaultFilters: string[];
  setDefaultFilters?: (filters: string[]) => void;
}) => {
  const { defaultFilters, setDefaultFilters } = props;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <IconButton
          icon={Settings01Icon}
          color="secondary"
          label="Manage filters"
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        className="flex flex-col gap-2 px-3 py-2"
      >
        <div className="text-sm font-medium">Manage filters</div>
        {setDefaultFilters &&
          DEFAULT_FILTERS.map((filter) => (
            <FilterSetting
              key={filter}
              defaultFilter={filter}
              onToggleDefaultFilter={(defaultFilter) => {
                const defaultFiltersSet = new Set(defaultFilters);
                if (defaultFiltersSet.has(defaultFilter)) {
                  setDefaultFilters(
                    defaultFilters.filter((f) => f !== defaultFilter)
                  );
                } else {
                  const newFilters = Array.from(
                    new Set([...defaultFilters, defaultFilter])
                  ).sort(
                    (a, b) =>
                      DEFAULT_FILTERS.indexOf(a) - DEFAULT_FILTERS.indexOf(b)
                  );
                  setDefaultFilters(newFilters);
                }
              }}
              active={defaultFilters.includes(filter)}
            />
          ))}
        {props.savedFilters.map((fv) => (
          <FilterSetting
            key={fv.id}
            filterView={fv}
            onToggleSwitch={props.onToggleSwitch}
          />
        ))}
      </PopoverContent>
    </Popover>
  );
};
